import { TFunction } from 'i18next';

import { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';
import { TripLegExceptionLogEntry } from '@/data/models/trip-leg.model';
import { TripLegDriverLocation } from '@/data/models/reservation.model';
import {
  TripLegStatus,
  tripStatusWithEtaList,
} from '@/data/models/trip-leg-status.model';

export type MonitoringExceptionRow = TripLegSearchResponseItem & {
  tripLegExceptionLogs?: TripLegExceptionLogEntry[] | null;
  tripLegDriverLocations?: TripLegDriverLocation[] | null;
};

// Values derived once per row whenever the trip legs change, so the cell
// renderers don't redo the work on every grid render.
export type MonitoringExceptionDerivedRow = MonitoringExceptionRow & {
  timeOnListStartMillis: number | null;
  etaMinutes: number[];
  showEta: boolean;
};

const hasExceptionID = (entry: TripLegExceptionLogEntry): boolean =>
  entry.tripLegExceptionID !== null && entry.tripLegExceptionID !== undefined;

// The trip leg has been on the list since the first exception entry after the
// most recent cleared (null exception) entry, or since its first exception
// entry if it was never cleared.
export const getTimeOnListStartMillis = (
  logs?: TripLegExceptionLogEntry[] | null,
): number | null => {
  if (!logs || logs.length === 0) {
    return null;
  }

  const sortedLogs = logs
    .map((entry) => ({ entry, millis: Date.parse(entry.createdOnUTC) }))
    .sort((a, b) => a.millis - b.millis);

  let startIndex = -1;
  for (let i = sortedLogs.length - 1; i >= 0; i -= 1) {
    if (!hasExceptionID(sortedLogs[i].entry)) {
      startIndex = i + 1;
      break;
    }
  }

  if (startIndex === -1) {
    startIndex = sortedLogs.findIndex(({ entry }) => hasExceptionID(entry));
  }

  if (startIndex === -1 || startIndex >= sortedLogs.length) {
    return null;
  }

  const { millis } = sortedLogs[startIndex];
  return Number.isNaN(millis) ? null : millis;
};

export const formatTimeOnList = (
  startMillis: number | null,
  nowMillis: number,
): string => {
  if (startMillis === null) {
    return '-';
  }
  const totalMinutes = Math.floor(Math.abs(nowMillis - startMillis) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}`;
};

export const getEtaMinutes = (
  driverLocations?: TripLegDriverLocation[] | null,
): number[] =>
  (driverLocations ?? [])
    .map((location) => location.estimatedTimeOfArrivalSeconds)
    .filter(Boolean)
    .map((seconds) => Math.round(seconds / 60));

export const shouldShowEta = (tripLegStatusBC?: string | null): boolean =>
  tripStatusWithEtaList.includes(
    tripLegStatusBC?.toLowerCase() as TripLegStatus,
  );

export const deriveMonitoringExceptionRow = (
  row: MonitoringExceptionRow,
): MonitoringExceptionDerivedRow => ({
  ...row,
  timeOnListStartMillis: getTimeOnListStartMillis(row.tripLegExceptionLogs),
  etaMinutes: getEtaMinutes(row.tripLegDriverLocations),
  showEta: shouldShowEta(row.tripLegStatusBC),
});

export const getExceptionName = (
  exceptionCode: string | null | undefined,
  t: TFunction<'common'>,
): string => {
  if (!exceptionCode) return '';

  switch (exceptionCode.toUpperCase()) {
    case 'NODRIVER':
      return t('tripLegException.noDriver');
    case 'DRIVERNOTONLINE':
      return t('tripLegException.driverNotOnline');
    case 'DRIVERLATE':
      return t('tripLegException.driverLate');
    case 'OPENSAMEDAY':
      return t('tripLegException.open');
    default:
      return exceptionCode;
  }
};
